<template>
  <div class="flex flex-col">
    <!-- WIP banner -->
    <div class="px-4 py-2 bg-yellow-50 border-b border-yellow-200 text-xs text-yellow-900">
      <FontAwesomeIcon :icon="['fas', 'triangle-exclamation']" class="mr-1.5 text-yellow-600" />
      Work in progress — UI and behavior are still being refined. Expect changes.
    </div>

    <!-- Row 1: Title + count + View + Apply + Export (sticky to viewport) -->
    <div class="px-4 py-2.5 bg-white flex items-center gap-3 sticky top-0 z-30" style="border-bottom: 1px solid #E8E9E9;">
      <h2 class="text-lg font-semibold" style="color: #353537;">
        CDI Operations
        <span class="text-xs font-medium text-yellow-700 ml-1">(beta)</span>
      </h2>

      <span class="text-xs" style="color: #626264;">
        {{ activeRows.length.toLocaleString() }}
        <template v-if="!showGroupTable && oppTotalRows > opportunityRows.length">
          of {{ oppTotalRows.toLocaleString() }}
        </template>
        {{ showGroupTable ? 'groups' : grainLabel }}
      </span>

      <div class="flex-1"></div>

      <!-- View Selector (#1391) -->
      <div class="relative">
        <div
          v-if="showViewMenu"
          class="fixed inset-0 z-10"
          @click="closeViewMenu"
        ></div>
        <button
          @click="showViewMenu = !showViewMenu"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-white hover:bg-gray-50 transition-colors"
          style="border: 1px solid #E8E9E9; color: #353537;"
        >
          <span style="color: #A5A5A6;">View:</span>
          <FontAwesomeIcon v-if="isPresetView" :icon="['fal', 'diamond']" class="text-xs -mr-0.5" style="color: #A5A5A6;" />
          <span>{{ currentViewLabel }}</span>
          <span
            v-if="viewDirty"
            class="text-xs px-1 rounded"
            style="background-color: #FEF3F0; color: #F76734;"
          >modified</span>
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
        </button>
        <div
          v-if="showViewMenu"
          class="absolute top-full right-0 mt-1 bg-white rounded-md shadow-lg z-20"
          style="border: 1px solid #E8E9E9; min-width: 240px;"
        >
          <div class="px-3 py-1.5 text-xs uppercase tracking-wide" style="color: #A5A5A6;">Presets</div>
          <button
            v-for="v in builtinViews"
            :key="v.key"
            @click="selectView(v.key)"
            class="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center justify-between"
            :style="currentViewKey === v.key ? { backgroundColor: '#FEF3F0', color: '#F76734', fontWeight: 600 } : { color: '#353537' }"
          >
            <span class="inline-flex items-center gap-1.5">
              <FontAwesomeIcon v-if="v.locked" :icon="['fal', 'diamond']" class="text-xs" style="color: #A5A5A6;" />
              {{ v.name }}
            </span>
            <span v-if="currentViewKey === v.key" class="text-xs">✓</span>
          </button>

          <div v-if="userViews.length" class="px-3 py-1.5 text-xs uppercase tracking-wide mt-1" style="color: #A5A5A6; border-top: 1px solid #F3F4F6;">My Views</div>
          <div
            v-for="v in userViews"
            :key="v.key"
            class="px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center justify-between cursor-pointer"
            :style="currentViewKey === v.key ? { backgroundColor: '#FEF3F0', color: '#F76734', fontWeight: 600 } : { color: '#353537' }"
            @click="selectView(v.key)"
          >
            <span>{{ v.name }}</span>
            <button
              @click.stop="deleteUserView(v.key)"
              class="text-xs px-1.5 hover:bg-gray-200 rounded"
              style="color: #9CA3AF;"
              title="Delete view"
            >×</button>
          </div>

          <div class="px-2 py-2" style="border-top: 1px solid #F3F4F6;">
            <div v-if="!showSaveAsInput" class="flex flex-col gap-1">
              <button
                @click="openSaveAs"
                class="w-full text-left px-2 py-1 text-xs rounded hover:bg-gray-50"
                style="color: #353537;"
              >+ Save current as…</button>
              <button
                v-if="canSaveOverwrite && viewDirty"
                @click="overwriteCurrentView"
                class="w-full text-left px-2 py-1 text-xs rounded hover:bg-gray-50"
                style="color: #353537;"
              >Save changes to "{{ currentView.name }}"</button>
            </div>
            <div v-else class="flex gap-1">
              <input
                v-model="saveAsName"
                @keyup.enter="confirmSaveAs"
                @keyup.escape="cancelSaveAs"
                ref="saveAsInput"
                placeholder="View name"
                class="flex-1 px-2 py-1 text-xs rounded"
                style="border: 1px solid #E8E9E9;"
              />
              <button
                @click="confirmSaveAs"
                :disabled="!saveAsName.trim()"
                class="px-2 py-1 text-xs rounded"
                style="background-color: #F76734; color: white;"
              >Save</button>
              <button
                @click="cancelSaveAs"
                class="px-2 py-1 text-xs rounded"
                style="color: #626264;"
              >×</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Apply Button (#1391) -->
      <button
        @click="applyPending"
        :disabled="!pendingChanges"
        class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
        :style="pendingChanges
          ? { backgroundColor: '#F76734', color: 'white', border: '1px solid #F76734' }
          : { backgroundColor: '#F9FAFB', color: '#A5A5A6', border: '1px solid #E8E9E9', cursor: 'not-allowed' }"
      >
        Apply{{ pendingChanges ? ' •' : '' }}
      </button>

      <!-- Export -->
      <ExportMenu :disabled="!groupRows.length" :exports="[
        { type: 'csv', action: exportCurrentGrain, loading: exportLoading },
        ...(showGroupTable ? [{ type: 'csv', action: exportDetails, label: 'Details', loading: exportLoading }] : [])
      ]" />
      <span v-if="exportError" class="text-xs" style="color: #DC2626;">{{ exportError }}</span>
      <span v-else-if="exportTruncated" class="text-xs" style="color: #D97706;">Export stopped at {{ exportRowLimitLabel }} rows. Narrow the filters to get the rest.</span>
    </div>

    <!-- Row 2: Program tabs (left) + Grain toggle (right) -->
    <div class="px-4 pb-2 bg-white flex items-center gap-1" style="border-bottom: 1px solid #E8E9E9;">
      <div class="flex items-center gap-1">
        <button
          v-for="tab in programTabs"
          :key="tab.value === null ? 'all' : tab.value"
          @click="selectProgramTab(tab.value)"
          class="px-3 py-1.5 text-xs font-medium transition-colors relative"
          :style="isActiveProgramTab(tab.value)
            ? { color: '#F76734', fontWeight: 600 }
            : { color: '#626264' }"
        >
          {{ tab.label }}
          <span
            v-if="isActiveProgramTab(tab.value)"
            class="absolute left-3 right-3 bottom-0"
            style="height: 2px; background-color: #F76734;"
          ></span>
        </button>
      </div>

      <div class="flex-1"></div>

      <!-- Grain Toggle -->
      <div class="flex items-center gap-0.5 rounded-lg p-0.5" style="background-color: #F3F4F6;">
        <button
          v-for="g in ['opportunity', 'task', 'code']"
          :key="g"
          @click="setGrain(g)"
          class="px-2.5 py-1 text-xs rounded-md transition-colors"
          :style="activeGrain === g
            ? { backgroundColor: '#fff', color: '#353537', fontWeight: 600, boxShadow: '0 1px 2px rgba(0,0,0,0.06)' }
            : { color: '#626264' }"
        >{{ { opportunity: 'Opportunities', task: 'Tasks', code: 'Codes' }[g] }}</button>
      </div>

      <!-- Grain info popover -->
      <span
        class="relative inline-flex ml-1.5"
        @mouseenter="grainInfoHover = true"
        @mouseleave="grainInfoHover = false"
      >
        <button
          type="button"
          class="inline-flex items-center justify-center w-4 h-4"
          style="color: #A5A5A6;"
          aria-label="What each grain shows"
          @click="grainInfoPinned = !grainInfoPinned"
        >
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
        </button>
        <div v-if="grainInfoPinned" class="fixed inset-0 z-20" @click="grainInfoPinned = false"></div>
        <div
          v-if="grainInfoHover || grainInfoPinned"
          class="absolute top-full right-0 mt-1 z-30 p-3 rounded-md bg-white text-left"
          style="border: 1px solid #E8E9E9; box-shadow: 0 4px 12px rgba(0,0,0,0.10); width: 340px;"
        >
          <div class="text-xs font-semibold mb-2" style="color: #353537;">What each grain shows</div>
          <div class="flex flex-col gap-2">
            <div>
              <div class="text-xs font-semibold" :style="{ color: activeGrain === 'opportunity' ? '#F76734' : '#353537' }">Opportunities</div>
              <div class="text-xs leading-relaxed" style="color: #626264;">One row per chart flagged for review (suspect or recapture). The date range shows opportunities that <span class="font-medium">closed</span> in the window, plus any still open.</div>
            </div>
            <div>
              <div class="text-xs font-semibold" :style="{ color: activeGrain === 'task' ? '#F76734' : '#353537' }">Tasks</div>
              <div class="text-xs leading-relaxed" style="color: #626264;">One row per review step (1LR → 2LR → 3LR → Confirm). The date range shows tasks <span class="font-medium">completed or expired</span> in the window, plus any still open.</div>
            </div>
            <div>
              <div class="text-xs font-semibold" :style="{ color: activeGrain === 'code' ? '#F76734' : '#353537' }">Codes</div>
              <div class="text-xs leading-relaxed" style="color: #626264;">One row per diagnosis code a coder added. The date range shows codes <span class="font-medium">added</span> in the window (strictly by add-date — no open carve-out).</div>
            </div>
          </div>
        </div>
      </span>
    </div>

    <!-- Row 3: FilterBar (entity filters) + grain-specific filters inline -->
    <Worklist_FilterBar
      storage-key="coding_ops_entity_filter"
      :info-tag-types="[{ type: 'Care Gap Practice Access', label: 'EMR Access', color: 'blue' }]"
      @filter-changed="onEntityFilterChanged"
    >
      <template #filters>
        <div class="flex items-center gap-1.5">
        <div class="flex items-center gap-1.5">
          <label class="text-xs whitespace-nowrap" style="color: #A5A5A6;">Perf. Year</label>
          <div style="width: 110px;">
            <SimpleMultiSelect
              v-model="activePerformanceYearFilter"
              :options="performanceYearOptions"
              :sensitive-labels="false"
              placeholder="All"
              @change="markPending"
            />
          </div>
        </div>

        <div v-if="activeGrain === 'opportunity'" class="flex items-center gap-1.5">
          <label class="text-xs whitespace-nowrap" style="color: #A5A5A6;">Status</label>
          <div style="width: 120px;">
            <SimpleMultiSelect
              v-model="activeStatusFilter"
              :options="grainStatusOptions"
              :sensitive-labels="false"
              placeholder="All"
              @change="markPending"
            />
          </div>
        </div>

        <div v-if="activeGrain === 'code'" class="flex items-center gap-1.5">
          <label class="text-xs whitespace-nowrap" style="color: #A5A5A6;">QA Result</label>
          <div style="width: 130px;">
            <SimpleMultiSelect
              v-model="activeQaResultFilter"
              :options="qaResultOptions"
              :sensitive-labels="false"
              placeholder="All"
              @change="markPending"
            />
          </div>
        </div>

        <div class="flex items-center gap-1.5">
          <label class="text-xs whitespace-nowrap" style="color: #A5A5A6;">Level</label>
          <div style="width: 100px;">
            <SimpleMultiSelect
              v-model="activeTierFilter"
              :options="tierOptions"
              :sensitive-labels="false"
              placeholder="All"
              @change="markPending"
            />
          </div>
        </div>

        <!-- Coder filter is task/code grain only — an opportunity has no coder,
             and the backend rejects a coder filter on that grain (400). -->
        <div v-if="availableCoders.length && activeGrain !== 'opportunity'" class="flex items-center gap-1.5">
          <label class="text-xs whitespace-nowrap" style="color: #A5A5A6;">Coder</label>
          <div style="width: 150px;">
            <SimpleMultiSelect
              v-model="activeCoderFilter"
              :options="coderOptions"
              :sensitive-labels="true"
              placeholder="All"
              :searchable="true"
              @change="markPending"
            />
          </div>
        </div>

        <div v-if="activeGrain === 'opportunity'">
          <label
            class="flex items-center gap-1.5 cursor-pointer text-xs whitespace-nowrap"
            :style="{ color: activeStaleOnly ? '#DC2626' : '#A5A5A6' }"
          >
            <input
              type="checkbox"
              v-model="activeStaleOnly"
              @change="markPending"
              class="rounded"
            />
            Stale only
          </label>
        </div>

        <!-- Expired and admin-closed are properties of the OPPORTUNITY, inherited by
             its tasks and codes, so both apply on every grain — unchecking either
             hides the chart together with its tasks and its codes. -->
        <div>
          <label
            class="flex items-center gap-1.5 cursor-pointer text-xs whitespace-nowrap"
            :style="{ color: activeIncludeExpired ? '#353537' : '#A5A5A6' }"
            title="Resolved on the OPPORTUNITY, on every grain — an expired opp hides its tasks and codes too. Task-level expiry is a separate thing and is not filtered here."
          >
            <input
              type="checkbox"
              v-model="activeIncludeExpired"
              @change="markPending"
              class="rounded"
            />
            Include expired opps
          </label>
        </div>

        <div>
          <label
            class="flex items-center gap-1.5 cursor-pointer text-xs whitespace-nowrap"
            :style="{ color: activeAdminClosed ? '#353537' : '#A5A5A6' }"
            title="Administrative bulk-close sweeps rather than completed review work. Resolved on the OPPORTUNITY, on every grain — an opp is admin-closed when ANY of its tasks carries an administrative-close action, and hiding it hides its tasks and codes too."
          >
            <input
              type="checkbox"
              v-model="activeAdminClosed"
              @change="markPending"
              class="rounded"
            />
            Include admin-closed opps
          </label>
        </div>

        </div>
        <div class="flex items-center gap-1.5">
          <label class="text-xs whitespace-nowrap" style="color: #A5A5A6;">Date</label>
          <input
            type="date"
            v-model="dateStart"
            @change="onDateInputChange"
            class="px-2 py-1 text-xs rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-primary-500"
            style="border: 1px solid #E8E9E9; color: #353537;"
          />
          <span class="text-xs" style="color: #A5A5A6;">to</span>
          <input
            type="date"
            v-model="dateEnd"
            @change="onDateInputChange"
            class="px-2 py-1 text-xs rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-primary-500"
            style="border: 1px solid #E8E9E9; color: #353537;"
          />
          <button
            v-for="preset in datePresets"
            :key="preset.key"
            @click="setDatePreset(preset.key)"
            class="px-2 py-1 text-xs rounded-md transition-colors"
            :style="datePresetStyle(preset.key)"
          >{{ preset.label }}</button>
        </div>
      </template>
    </Worklist_FilterBar>

    <!-- Row 4: Group chips -->
    <div class="px-4 py-2 bg-white flex items-center flex-wrap gap-1.5" style="border-bottom: 1px solid #E8E9E9;">
      <label class="text-xs font-medium whitespace-nowrap mr-1" style="color: #626264;">Group by</label>
      <div class="flex items-center flex-wrap gap-1.5">
      <button
        v-for="opt in groupByOptions"
        :key="opt.value"
        @click="toggleGroupChip(opt.value)"
        class="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full transition-colors"
        :style="groupChipStyle(opt.value)"
      >
        <span
          v-if="groupChips.includes(opt.value)"
          class="inline-flex items-center justify-center rounded-full text-white"
          style="width: 14px; height: 14px; font-size: 9px; font-weight: 700; background-color: #F76734;"
        >{{ groupChips.indexOf(opt.value) + 1 }}</span>
        {{ opt.label }}
      </button>
      </div>
      <span
        v-if="isPresetView && !presetStructureModified"
        class="inline-flex items-center gap-1.5 text-xs ml-2"
        style="color: #A5A5A6;"
      >
        <FontAwesomeIcon :icon="['fal', 'diamond']" />
        Preset report — adjust date or practice scope to refine. Adding filters narrows aggregates, changing grain or grouping resets columns.
      </span>
      <span
        v-if="isPresetView && presetStructureModified"
        class="inline-flex items-center gap-1.5 text-xs ml-2"
        style="color: #F76734;"
      >
        <FontAwesomeIcon :icon="['fas', 'triangle-exclamation']" />
        Modified from preset — columns show default labels. Re-select the view to restore.
      </span>
    </div>

    <!-- Methodology Banner (compact) -->
    <div
      class="mx-3 mt-2 px-3 py-1.5 rounded-md text-xs"
      style="background-color: #F8FAFC; border: 1px solid #E2E8F0; color: #475569;"
    >
      <span class="font-semibold" style="color: #353537;">{{ methodologyBanner.title }}.</span>
      {{ methodologyBanner.body }}
    </div>

    <!-- Main Content Area. No nested scroll/clip context here: the page scrolls
         in the app-shell container, and the table header (sticky on its <th>)
         pins to that same scroller, just below the sticky title bar. -->
    <div class="flex">
      <!-- Left Navigation Panel (visible when drilled into a group) -->
      <div v-if="selectedGroup" class="w-56 flex flex-col gap-1.5 p-3 overflow-y-auto shrink-0">
        <div
          @click="navigateBack"
          class="px-3 py-2 rounded-lg cursor-pointer transition-colors"
          style="background-color: #F9FAFB; border-left: 3px solid transparent;"
        >
          <div class="flex items-center gap-2">
            <span class="text-xs" style="color: #A5A5A6;">◀</span>
            <span class="text-xs font-medium" style="color: #353537;">{{ groupByLabel }}</span>
          </div>
        </div>
        <div class="px-3 py-2 rounded-lg" style="background-color: #FEF3F0; border-left: 3px solid #F76734;">
          <div class="text-xs font-medium truncate" style="color: #F76734;">
            {{ groupChips.map((_, i) => formatGroupCell(`group_${i + 1}`, selectedGroup[`group_${i + 1}`])).join(' / ') }}
          </div>
          <div class="text-xs mt-1" style="color: #A5A5A6;">
            {{ (selectedGroup[activeGrain === 'task' ? 'task_count' : activeGrain === 'code' ? 'code_count' : 'opportunity_count'] || 0).toLocaleString() }} {{ grainLabel }}
          </div>
        </div>
      </div>

      <!-- Content + Side Panel -->
      <div class="flex-1 flex min-w-0">
        <!-- Content Column -->
        <div
          class="transition-all duration-300 flex flex-col"
          :class="selectedOpportunity ? 'w-3/5' : 'w-full'"
        >
          <!-- Data Table (shared between Level 0 and Level 1) -->
          <div
            class="flex-1 flex flex-col m-3 relative"
            style="border: 1px solid #E8E9E9; border-radius: 8px; background-color: #FFFFFF;"
          >
            <!-- Stale overlay: filter changes pending — table is showing pre-change data.
                 Button anchored to top so it stays visible on tall tables. -->
            <div
              v-if="pendingChanges && !tableLoading"
              @click="applyPending"
              class="absolute inset-0 z-10 flex items-start justify-center pt-6 cursor-pointer"
              style="background-color: rgba(255, 255, 255, 0.65); backdrop-filter: blur(1px);"
            >
              <button
                @click.stop="applyPending"
                class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md shadow-md transition-colors"
                style="background-color: #F76734; color: white;"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
                Apply to refresh
              </button>
            </div>
            <div v-if="tableLoading" class="p-4">
              <LoadingIndicator :message="showGroupTable ? 'Loading groups...' : 'Loading ' + grainLabel + '...'" />
            </div>
            <div v-else class="flex-1">
              <table class="w-full text-xs">
                <thead style="color: #626264;">
                  <tr>
                    <th
                      v-for="col in activeColumns"
                      :key="col.field || col.header"
                      @click="toggleSort(col.field)"
                      class="py-2.5 px-4 font-medium cursor-pointer select-none hover:text-gray-900"
                      :class="col.align === 'left' ? 'text-left' : 'text-right'"
                      style="position: sticky; top: 49px; z-index: 2; background-color: #FFFFFF; border-bottom: 1px solid #E8E9E9;"
                    >
                      <span class="inline-flex items-center gap-1" :class="col.align !== 'left' ? 'justify-end' : ''">
                        {{ col.header }}
                        <span class="text-xs">{{ sortIndicator(col.field) }}</span>
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, idx) in activeRows"
                    :key="showGroupTable ? idx : (row.opportunity_id || row.action_id || row.work_id || idx)"
                    @click="onRowClick(row)"
                    :class="(showGroupTable && !drilldownEnabled) ? '' : 'cursor-pointer hover:bg-gray-50'"
                    :style="{
                      borderBottom: '1px solid #E8E9E9',
                      backgroundColor: !showGroupTable && selectedOpportunity && selectedOpportunity[grainIdField] === row[grainIdField] ? '#FEF3F0' : '',
                    }"
                  >
                    <td
                      v-for="col in activeColumns"
                      :key="col.field || col.header"
                      class="py-3 px-4"
                      :class="[col.align === 'left' ? 'text-left' : 'text-right', col.blur || '']"
                      :style="cellStyle(col, cellValue(col, row))"
                    >
                      <template v-if="showGroupTable && col.field && col.field.startsWith('group_')">
                        <span class="sensitive" style="color: #353537;">{{ formatGroupCell(col.field, row[col.field]) }}</span>
                        <span v-if="col.field === 'group_1' && row.group_1 && drilldownEnabled" class="text-xs ml-1.5" style="color: #A5A5A6;">▶</span>
                      </template>
                      <template v-else-if="col.field === 'patient_name'">
                        <span class="sensitive" :title="row.patient_identifier">{{ row.patient_name }}</span>
                      </template>
                      <template v-else-if="col.field === 'tier_progression'">
                        <div class="flex items-center gap-1">
                          <span
                            v-for="tier in parseTierProgression(row)"
                            :key="tier.name"
                            class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs"
                            :style="tierBadgeStyle(tier)"
                          >
                            {{ tier.name }}
                            <svg v-if="tier.completed" class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                            </svg>
                            <svg v-else-if="tier.ended" class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
                            </svg>
                          </span>
                        </div>
                      </template>
                      <DemoText v-else-if="col.freeText" :text="formatCell(col, cellValue(col, row))" :record="row.patient_identifier" />
                      <template v-else>
                        {{ formatCell(col, cellValue(col, row)) }}
                      </template>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-if="activeError" class="text-center py-8" style="color: #DC2626;">
                {{ activeError }}
              </div>
              <div v-else-if="activeRows.length === 0" class="text-center py-8" style="color: #626264;">
                {{ showGroupTable ? 'No groups found' : 'No ' + grainLabel + ' found' }}
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="px-4 py-2 mx-3 mb-1 flex items-center justify-between text-xs" style="color: #626264;">
            <span>
              {{ activeRows.length }} {{ showGroupTable ? 'groups' : grainLabel }}
              <template v-if="!showGroupTable && oppTotalRows > opportunityRows.length">
                ({{ oppTotalRows.toLocaleString() }} total — apply filters to narrow)
              </template>
            </span>

          </div>
        </div>

        <!-- Side Panel -->
        <transition
          enter-active-class="transition-all duration-300"
          leave-active-class="transition-all duration-300"
          enter-from-class="opacity-0 translate-x-4"
          leave-to-class="opacity-0 translate-x-4"
        >
          <div
            v-if="selectedOpportunity"
            class="w-2/5 flex flex-col overflow-hidden"
            style="border-left: 1px solid #E8E9E9;"
          >
            <Coding_Ops_Detail_Panel
              :opportunity-data="selectedOpportunity"
              :tasks="selectedOpportunity.tasks || []"
              :work-items="selectedOpportunity.work_items || []"
              :grain="activeGrain"
              :program-labels="programLabels"
              @close="selectedOpportunity = null"
            />
          </div>
        </transition>
      </div>
    </div>

  </div>
</template>

<script>
import Coding_Ops_Detail_Panel from './panels/Coding_Ops_Detail_Panel.vue';
import DemoText from '@/components/DemoMode/DemoText.vue';
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import SimpleMultiSelect from '@/components/widgetcomponents/Dropdown_Selector/SimpleMultiSelect.vue';
import Worklist_FilterBar from '@/components/widgetcomponents/Worklist_FilterBar.vue';
import {
  BUILTIN_PRESETS,
  PROGRAM_LABELS,
  formatProgram,
  resolveGroupChip,
  serializeGroupChip,
  loadUserViews,
  saveUserViews,
  loadLastViewKey,
  saveLastViewKey,
} from './Coding_Operations_V2_Views.js';
import { beginSensitiveEgress } from '@/utils/demoEgress';

// Programs surfaced in V2. Resub is intentionally excluded — Vytalize CDI
// scope is Suspect + Early Start only. "All" tab sends this list explicitly
// so aggregates exclude Resub instead of summing across all programs.
const VISIBLE_PROGRAMS = ['Suspect', 'Early Start'];

// -- Task grain aggregate columns (worklist + throughput) --
// 'completed' mirrors the Portal worklist; 'Actions' is the old task-action
// tally, demoted from headline to a secondary churn metric.
const TASK_AGG_COLUMNS = [
  { header: 'Tasks', field: 'task_count' },
  { header: 'Active', field: 'active_count' },
  { header: 'Completed', field: 'completed_count', color: v => v > 0 ? '#389A6C' : null },
  { header: 'On Hold', field: 'on_hold_count', color: v => v > 0 ? '#D97706' : null },
  { header: 'Snoozed', field: 'snoozed_count' },
  { header: 'Skipped', field: 'skipped_count', color: v => v > 0 ? '#9CA3AF' : null },
  { header: 'Expired', field: 'expired_count', color: v => v > 0 ? '#9CA3AF' : null },
  { header: 'Completion %', field: 'completion_rate', format: 'pct',
    color: v => { if (v == null) return null; return v >= 0.8 ? '#389A6C' : v < 0.4 ? '#DC2626' : '#D97706'; },
    bold: v => v != null },
  { header: 'Avg TAT', field: 'avg_turnaround_days', small: true },
  { header: 'Avg Open Age', field: 'avg_open_age_days', small: true },
  { header: 'Actions', field: 'action_count' },
  { header: 'Rework %', field: 'rework_rate', format: 'pct',
    color: v => { if (v == null) return null; return v > 0.2 ? '#DC2626' : v > 0.1 ? '#D97706' : '#389A6C'; } },
  { header: 'Patients', field: 'patient_count' },
];

// -- Task grain detail columns --
// Full set of task-row fields the backend returns (TASK_DETAIL_COLS in
// cdi_reporting_service.py) — the show-everything default the 'Explore' view uses.
const EXPORT_ROW_LIMIT = 50000;

const TASK_STATE_LABELS = {
  active: 'Active', completed: 'Completed', on_hold: 'On Hold',
  snoozed: 'Snoozed', skipped: 'Skipped', expired: 'Expired', inactive: 'Inactive',
};
const TASK_DETAIL_COLUMNS = [
  { header: 'Patient', field: 'patient_name', align: 'left', blur: 'sensitive' },
  { header: 'MBI', field: 'patient_identifier', align: 'left', blur: 'sensitive' },
  { header: 'Program', field: 'program', align: 'center',
    format: formatProgram,
    color: v => ({ Suspect: '#4C6E97', Resub: '#9F4D30', 'Early Start': '#7C3AED' }[v]), bold: v => !!v },
  { header: 'Level', field: 'tier', align: 'center' },
  { header: 'Status', field: 'task_state', align: 'center',
    format: v => TASK_STATE_LABELS[v] || v,
    color: v => ({ completed: '#389A6C', active: '#0E8C9E', on_hold: '#D97706', expired: '#9CA3AF', skipped: '#9CA3AF' }[v] || null) },
  // 'Assigned Coder' removed 2026-08-03 — it read an OpportunityTaskTeam owner
  // with no claim check, so it showed push-assignment artifacts. Retired to NULL
  // upstream. Who did the work is the actioning coder, below.
  { header: 'Coding Analyst', field: 'coder_entity_name', align: 'left', blur: 'sensitive' },
  { header: 'Actions', field: 'action_count' },
  { header: 'Last Action', field: 'action_name', align: 'left' },
  { header: 'Last Hold Reason', field: 'hold_reason', align: 'left', freeText: true },
  { header: 'Held On', field: 'hold_at', align: 'center', format: v => v ? String(v).slice(0, 10) : '' },
  { header: 'Created', field: 'task_created_at', align: 'center', format: v => v ? String(v).slice(0, 10) : '' },
  { header: 'Completed', field: 'task_completed_at', align: 'center', format: v => v ? String(v).slice(0, 10) : '' },
  { header: 'TAT', field: 'turnaround_days', align: 'center' },
  { header: 'Age', field: 'age_days', align: 'center' },
  { header: 'Practice', field: 'practice_name', align: 'left', blur: 'sensitive' },
];

// -- Code grain aggregate columns --
const CODE_AGG_COLUMNS = [
  { header: 'Patients', field: 'patient_count' },
  { header: 'Codes', field: 'code_count' },
  { header: 'QA Reviewed', field: 'qa_reviewed_count' },
  { header: 'QA Accepted', field: 'qa_accepted', color: v => v > 0 ? '#389A6C' : null },
  { header: 'QA Rejected', field: 'qa_rejected', color: v => v > 0 ? '#DC2626' : null },
  { header: 'QA Pending', field: 'qa_pending', color: v => v > 0 ? '#D97706' : null },
  { header: 'QA Accept %', field: 'qa_rate', format: 'pct',
    color: v => { if (v == null) return null; return v >= 0.9 ? '#389A6C' : v < 0.7 ? '#DC2626' : '#D97706'; },
    bold: v => v != null },
  { header: 'QA Reject %', field: 'reject_rate', format: 'pct',
    color: v => { if (v == null) return null; return v > 0.2 ? '#DC2626' : v > 0.1 ? '#D97706' : '#389A6C'; },
    bold: v => v != null },
  { header: 'Suspects', field: 'suspect_count' },
  { header: 'Recaptures', field: 'recapture_count' },
  { header: 'MEAT Present', field: 'meat_present_count' },
  { header: 'Distinct ICDs', field: 'distinct_icd_codes' },
];

// -- Code grain detail columns --
// Complete set of code-row fields the backend returns (CODE_DETAIL_COLS in
// cdi_reporting_service.py). This is the un-renamed, show-everything default the
// 'Explore — All Data' view leans on; the locked reports define their own subset.
// Internal join keys (work_id, *_entity_id) are intentionally omitted.
const CODE_DETAIL_COLUMNS = [
  { header: 'Patient', field: 'patient_name', align: 'left', blur: 'sensitive' },
  { header: 'MBI', field: 'patient_identifier', align: 'left', blur: 'sensitive' },
  { header: 'DOB', field: 'patient_dob', align: 'left', blur: 'sensitive', format: v => v ? String(v).slice(0, 10) : '' },
  { header: 'ICD Code', field: 'icd_code', bold: () => true },
  { header: 'Dx Description', field: 'icd_description', align: 'left' },
  { header: 'HCC', field: 'hcc_code' },
  { header: 'Status', field: 'icd_status', align: 'left' },
  { header: 'Coder', field: 'coder_entity_name', align: 'left', blur: 'sensitive' },
  { header: 'Explanation', field: 'coder_explanation', align: 'left' },
  { header: 'QA Result', field: 'qa_concurrence', format: v => v === true ? 'Accept' : v === false ? 'Reject' : 'Pending',
    color: v => v === true ? '#389A6C' : v === false ? '#DC2626' : '#9CA3AF' },
  { header: 'Reject Reason', field: 'qa_disagree_reason', align: 'left' },
  { header: 'QA Explanation', field: 'qa_explanation', align: 'left' },
  { header: 'QA Reviewer', field: 'qa_reviewer_entity_name', align: 'left', blur: 'sensitive' },
  { header: 'PCP Name', field: 'pcp_name', align: 'left', blur: 'sensitive' },
  { header: 'PCP NPI', field: 'pcp_npi', align: 'left', blur: 'sensitive' },
  { header: 'Program', field: 'program', align: 'center',
    format: formatProgram,
    color: v => ({ Suspect: '#4C6E97', Resub: '#9F4D30', 'Early Start': '#7C3AED' }[v]) },
  { header: 'Level', field: 'tier', align: 'center' },
  { header: 'Practice', field: 'practice_name', align: 'left', blur: 'sensitive' },
  { header: 'TIN', field: 'practice_tin', align: 'left', blur: 'sensitive' },
  { header: 'Submitted', field: 'work_created_at', align: 'center', format: v => v ? String(v).slice(0, 10) : '' },
];

// -- Opportunity grain aggregate columns (same regardless of grouping dimensions) --
const OPP_AGG_COLUMNS = [
  { header: 'Patients', field: 'patient_count' },
  { header: 'Opps', field: 'opportunity_count' },
  { header: 'Active', field: 'active_count' },
  { header: 'Completed', field: 'completed_count', color: v => v > 0 ? '#389A6C' : null },
  { header: 'Expired', field: 'expired_count', color: v => v > 0 ? '#9CA3AF' : null },
  { header: 'Codes (QA Accepted)', field: 'qa_accepted', color: v => v > 0 ? '#389A6C' : null },
  { header: 'Code Yield', field: 'code_yield' },
  { header: 'QA Accept %', field: 'qa_rate', format: 'pct',
    color: v => { if (v == null) return null; return v >= 0.9 ? '#389A6C' : v < 0.7 ? '#DC2626' : '#D97706'; },
    bold: v => v != null },
  { header: 'No Find %', field: 'no_findings_pct', format: 'pct',
    color: v => { if (v == null) return null; return v > 0.5 ? '#DC2626' : v > 0.3 ? '#D97706' : '#389A6C'; } },
  { header: 'Codes Added %', field: 'codes_added_pct', format: 'pct',
    color: v => { if (v == null) return null; return v >= 0.1 ? '#389A6C' : '#9CA3AF'; } },
  { header: 'Avg TAT', field: 'avg_turnaround', small: true },
  { header: 'Stale', field: 'stale_count', color: v => v > 0 ? '#DC2626' : null, bold: v => v > 0 },
  { header: 'Suspects', field: 'suspect_total' },
  { header: 'Recaptures', field: 'recapture_total' },
];

// -- Opportunity table columns (Level 1 / Flat) --
const O = {
  patient: { header: 'Patient', field: 'patient_name', align: 'left', blur: 'sensitive' },
  // No 'Last Coder' here — an opportunity has no coder. Drill to the task grain
  // (who actioned each tier) or the code grain (who added each code).
  program: {
    header: 'Program', field: 'program', align: 'center', format: formatProgram,
    color: v => ({ Suspect: '#4C6E97', Resub: '#9F4D30', 'Early Start': '#7C3AED' }[v]),
    bold: v => !!v,
  },
  tier: { header: 'Task Type', field: 'current_tier', align: 'center' },
  chartStatus: {
    header: 'Chart', field: 'chart_status', align: 'center', format: 'capitalize',
    color: v => ({ completed: '#389A6C', in_progress: '#D97706', no_work: '#9CA3AF' }[v]),
  },
  stageDays: { header: 'Stage', field: 'time_in_current_stage' },
  stale: {
    header: 'Stale', field: 'is_stale', format: 'boolean',
    color: v => v ? '#DC2626' : null, bold: v => v,
  },
  codes: { header: 'Codes (QA Accepted)', field: 'qa_accepted', color: v => v > 0 ? '#389A6C' : null },
  suspects: { header: 'Suspects', field: 'suspect_code_count' },
  recaps: { header: 'Recaptures', field: 'recapture_code_count' },
  qaAcc: { header: 'QA Accepted', field: 'qa_accepted', color: v => v > 0 ? '#389A6C' : null },
  qaRej: { header: 'QA Rejected', field: 'qa_rejected', color: v => v > 0 ? '#DC2626' : null },
  qaRate: {
    header: 'QA Accept %', field: 'qa_rate', format: 'pct',
    color: v => { if (v == null) return null; return v >= 0.9 ? '#389A6C' : v < 0.7 ? '#DC2626' : '#D97706'; },
    bold: v => v != null,
  },

  progression: { header: 'Progression', field: 'tier_progression', align: 'left' },
  days: { header: 'Days', field: 'days_in_pipeline' },
  turnaround: { header: 'TAT', field: 'turnaround_days' },
  practice: { header: 'Practice', field: 'practice_name', align: 'left', blur: 'sensitive' },
};

const OPP_COLUMNS = [
  O.patient, O.program, O.stageDays, O.stale,
  O.codes, O.suspects, O.recaps, O.qaAcc, O.qaRej, O.qaRate,
  O.progression, O.days, O.turnaround,
];

export default {
  name: 'Coding_Operations_V2',
  components: { Coding_Ops_Detail_Panel, DemoText, ExportMenu, LoadingIndicator, SimpleMultiSelect, Worklist_FilterBar },
  data() {
    return {
      // Entity filters (owned locally now that the FilterBar is inline)
      entityFilters: null,
      infotagFilters: null,

      // Grain toggle
      activeGrain: 'opportunity',

      // Grain info popover
      grainInfoHover: false,
      grainInfoPinned: false,

      // Navigation
      selectedGroup: null,
      selectedOpportunity: null,
      _drillFilterSnapshot: null,

      // Level 0 (groups)
      groupRows: [],
      groupLoading: false,
      groupError: null,
      groupSortCol: 'group_1',
      groupSortDir: 'asc',

      // Level 1 / Flat (opportunities)
      opportunityRows: [],
      oppLoading: false,
      oppError: null,
      oppSortCol: 'days_in_pipeline',
      oppSortDir: 'desc',
      oppTotalRows: 0,

      // Filters
      groupChips: [],  // max 3 — first is GROUP BY dimension, others are sub-grouping
      activeMetricFilter: [],
      activeStatusFilter: [],
      activeTierFilter: [],

      activeCoderFilter: [],
      activeQaResultFilter: [],
      activePerformanceYearFilter: [],
      performanceYearOptions: [
        { value: 2025, label: '2025' },
        { value: 2026, label: '2026' },
      ],
      activeStaleOnly: false,
      activeIncludeExpired: false,
      activeAdminClosed: false,
      dateStart: null,
      dateEnd: null,
      activeDatePreset: null,
      datePresets: [
        { key: 'yesterday', label: 'Yesterday' },
        { key: 'mtd', label: 'MTD' },
        { key: 'last_month', label: 'Last Month' },
        { key: 'ytd', label: 'YTD' },
      ],
      entityTypes: [],
      availableCoders: [],

      exportLoading: false,
      exportTruncated: false,
      exportError: null,
      exportRowLimitLabel: EXPORT_ROW_LIMIT.toLocaleString(),

      // Saved views (#1391)
      userViews: loadUserViews(),
      currentViewKey: null,
      pendingChanges: false,
      showViewMenu: false,
      showSaveAsInput: false,
      saveAsName: '',
    };
  },

  computed: {
    activeGroupBy() {
      return this.groupChips[0] || null;
    },
    programLabels() {
      return PROGRAM_LABELS;
    },
    drilldownEnabled() {
      if (this.presetStructureModified) return true;
      return !(this.currentView && this.currentView.disableDrilldown);
    },
    showGroupTable() {
      return !this.selectedGroup;
    },
    tableLoading() {
      return this.showGroupTable ? this.groupLoading : this.oppLoading;
    },
    groupByOptions() {
      const opts = [{ value: 'program', label: 'Program' }];
      opts.push({ value: 'payer', label: 'Payer' });
      const practice = this.entityTypes.find(et => et.type_name === 'Practice');
      if (practice) opts.push({ value: practice.id, label: 'Practice' });
      opts.push({ value: 'tier', label: 'Level' });
      // Coder / Vendor are TASK and CODE grain only. A coding opportunity has no
      // owner — it spans tiers claimed one task at a time by different people —
      // so the backend rejects a coder dimension on the opportunity grain (400).
      // Offering the chip here would just serve up that error.
      if (this.activeGrain !== 'opportunity') {
        const vendor = this.entityTypes.find(et => et.type_name === 'Vendor');
        if (vendor) opts.push({ value: vendor.id, label: 'Vendor' });
        const coder = this.entityTypes.find(et => et.type_name === 'Coding Analyst');
        if (coder) opts.push({ value: coder.id, label: 'Coder' });
      }
      if (this.activeGrain === 'opportunity') {
        opts.push({ value: 'status', label: 'Status' });
      }
      return opts;
    },
    programOptions() {
      return [
        { value: 'Suspect', label: PROGRAM_LABELS['Suspect'] },
        { value: 'Early Start', label: PROGRAM_LABELS['Early Start'] },
      ];
    },
    programTabs() {
      return [{ value: null, label: 'All' }, ...this.programOptions];
    },
    statusOptions() {
      return [
        { value: 'active', label: 'Active' },
        { value: 'completed', label: 'Completed' },
        { value: 'expired', label: 'Expired' },
      ];
    },
    taskStatusOptions() {
      // Values map directly to TASK_STATE buckets on CODING_OPS_TASKS.
      return [
        { value: 'active', label: 'Active' },
        { value: 'completed', label: 'Completed' },
        { value: 'on_hold', label: 'On Hold' },
        { value: 'snoozed', label: 'Snoozed' },
        { value: 'skipped', label: 'Skipped' },
        { value: 'expired', label: 'Expired' },
      ];
    },
    grainStatusOptions() {
      return this.activeGrain === 'task' ? this.taskStatusOptions : this.statusOptions;
    },
    qaResultOptions() {
      return [
        { value: 'accepted', label: 'Accepted' },
        { value: 'rejected', label: 'Rejected' },
        { value: 'pending', label: 'Pending' },
      ];
    },

    tierOptions() {
      return [
        { value: '1LR', label: '1LR' },
        { value: '2LR', label: '2LR' },
        { value: '3LR', label: '3LR' },
        { value: 'Confirm', label: 'Confirm' },
      ];
    },
    coderOptions() {
      return this.availableCoders.map(name => ({ value: name, label: name }));
    },
    groupByLabel() {
      return this.groupChips.map(v => this.chipLabel(v)).join(' × ') || 'Groups';
    },
    grainAggColumns() {
      if (this.activeGrain === 'task') return TASK_AGG_COLUMNS;
      if (this.activeGrain === 'code') return CODE_AGG_COLUMNS;
      return OPP_AGG_COLUMNS;
    },
    grainDetailColumns() {
      if (this.activeGrain === 'task') return TASK_DETAIL_COLUMNS;
      if (this.activeGrain === 'code') return CODE_DETAIL_COLUMNS;
      return OPP_COLUMNS;
    },
    grainLabel() {
      return { opportunity: 'opportunities', task: 'tasks', code: 'codes' }[this.activeGrain];
    },
    grainIdField() {
      return { opportunity: 'opportunity_id', task: 'task_id', code: 'work_id' }[this.activeGrain];
    },
    activeColumns() {
      const usePresetCols = this.currentView && this.currentView.columns && !this.presetStructureModified;
      const presetCols = usePresetCols ? this.currentView.columns : null;
      const visible = c => !c.showIfState || c.showIfState(this);
      if (!this.showGroupTable) {
        const detail = (presetCols && presetCols.detail) || this.grainDetailColumns;
        return detail.filter(visible);
      }
      const groupCols = this.groupChips.map((chip, i) => ({
        header: this.chipLabel(chip),
        field: `group_${i + 1}`,
        align: 'left',
      }));
      const aggCols = ((presetCols && presetCols.aggregate) || this.grainAggColumns).filter(visible);
      return [...groupCols, ...aggCols];
    },
    activeRows() {
      if (!this.showGroupTable) {
        return this.sortData(this.opportunityRows, this.oppSortCol, this.oppSortDir);
      }
      // Group table: user-selected column is primary, group names as tiebreaker
      const col = this.groupSortCol;
      const d = this.groupSortDir === 'asc' ? 1 : -1;
      return [...this.groupRows].sort((a, b) => {
        const va = a[col], vb = b[col];
        if (va != null && vb != null && va !== vb) {
          return typeof va === 'string' ? d * va.localeCompare(vb) : d * (va - vb);
        }
        if (va == null && vb != null) return 1;
        if (va != null && vb == null) return -1;
        return (a.group_1 || '').localeCompare(b.group_1 || '')
          || (a.group_2 || '').localeCompare(b.group_2 || '');
      });
    },
    dateFilterLabel() {
      return {
        opportunity: 'Date resolved',
        task: 'Date completed',
        code: 'Date submitted',
      }[this.activeGrain];
    },
    methodologyBanner() {
      const banners = {
        opportunity: {
          title: 'Methodology: opportunity-level pipeline ownership',
          body: 'Each row represents a coding opportunity/patient-HCC workflow. Coder reflects the current assigned owner for the opportunity’s active task, based on assignment rules. Use this view to understand pipeline status, current ownership, backlog, and progression.',
        },
        task: {
          title: 'Methodology: task worklist & throughput',
          body: 'Each row is a single review task — including open tasks with no action yet — so counts reflect the live worklist, not just completed work. Completed = Closed Out / Closed Succeeded (matches the Portal); skipped auto-advances and expired tasks are tracked in their own buckets. “Coding Analyst” is who took the task’s latest action — coding is claimed one task at a time, so this is the only real coder signal; there is no separate task owner. Use this view for backlog, completion rate, turnaround, and per-coder/level/practice throughput.',
        },
        code: {
          title: 'Methodology: submitted code output and QA',
          body: 'Each row represents a submitted code suggestion. Coder reflects the person who added the code. Use this view to analyze coding output, QA acceptance/rejection, code quality, and coder-level accuracy.',
        },
      };
      return banners[this.activeGrain];
    },
    currentSortCol() {
      return this.showGroupTable ? this.groupSortCol : this.oppSortCol;
    },
    currentSortDir() {
      return this.showGroupTable ? this.groupSortDir : this.oppSortDir;
    },

    // -- Saved views (#1391) --
    builtinViews() {
      return BUILTIN_PRESETS.map(p => ({ ...p, builtin: true }));
    },
    allViews() {
      return [
        ...this.builtinViews,
        ...this.userViews.map(v => ({ ...v, builtin: false })),
      ];
    },
    currentView() {
      return this.allViews.find(v => v.key === this.currentViewKey) || null;
    },
    currentViewLabel() {
      return this.currentView ? this.currentView.name : 'Custom';
    },
    canSaveOverwrite() {
      return !!this.currentView && !this.currentView.builtin;
    },
    viewDirty() {
      if (!this.currentView) return false;
      return JSON.stringify(this._captureCurrentState()) !== JSON.stringify(this.currentView.state);
    },
    isPresetView() {
      return !!(this.currentView && this.currentView.locked);
    },
    presetStructureModified() {
      if (!this.currentView || !this.currentView.locked) return false;
      const s = this.currentView.state;
      if (this.activeGrain !== s.activeGrain) return true;
      const serialized = this.groupChips
        .map(c => serializeGroupChip(c, this.entityTypes))
        .filter(c => c != null);
      return JSON.stringify(serialized) !== JSON.stringify(s.groupChips || []);
    },
    // Error for the currently-shown level (groups vs detail). Surfaced in place
    // of the "No data" empty state so a failed request isn't mistaken for empty.
    activeError() {
      return this.showGroupTable ? this.groupError : this.oppError;
    },
  },

  methods: {
    onEntityFilterChanged(payload) {
      this.entityFilters = payload?.entity_filters || null;
      this.infotagFilters = payload?.infotag_filters || null;
      this.selectedOpportunity = null;
      this.markPending();
    },

    // Coder / Vendor dimensions do not exist on the opportunity grain — an
    // opportunity has no owner, and the backend rejects them with a 400. Strip
    // them whenever we land on that grain so neither a grain toggle nor a saved
    // view can fire a request that is guaranteed to fail.
    _stripCoderDimsIfOppGrain() {
      if (this.activeGrain !== 'opportunity') return;
      const coderTypeIds = ['Coding Analyst', 'Vendor']
        .map(n => this.entityTypes.find(et => et.type_name === n))
        .filter(Boolean)
        .map(et => et.id);
      this.groupChips = this.groupChips.filter(v => !coderTypeIds.includes(v));
      this.activeCoderFilter = [];
    },

    // -- Grain toggle --
    setGrain(grain) {
      if (this.activeGrain === grain) return;
      this.activeGrain = grain;
      this.selectedGroup = null;
      this.selectedOpportunity = null;
      this.opportunityRows = [];
      this.activeStatusFilter = [];
      this.activeQaResultFilter = [];
      this._stripCoderDimsIfOppGrain();
      this.fetchGroups();
    },

    // -- Group chips --
    toggleGroupChip(value) {
      const idx = this.groupChips.indexOf(value);
      if (idx >= 0) {
        this.groupChips.splice(idx, 1);
      } else if (this.groupChips.length < 3) {
        this.groupChips.push(value);
      } else {
        this.groupChips.shift();
        this.groupChips.push(value);
      }
      this.selectedGroup = null;
      this.selectedOpportunity = null;
      this.opportunityRows = [];
      this.markPending();
    },
    chipLabel(value) {
      if (value === 'program') return 'Program';
      if (value === 'tier') return 'Level';
      if (value === 'status') return 'Status';
      if (value === 'payer') return 'Payer';
      const et = this.entityTypes.find(e => e.id === value);
      return et ? et.type_name : 'Group';
    },
    groupChipStyle(value) {
      const active = this.groupChips.includes(value);
      return active
        ? { backgroundColor: '#FEF3F0', color: '#F76734', border: '1px solid #F76734', fontWeight: 600 }
        : { backgroundColor: '#F9FAFB', color: '#626264', border: '1px solid #E8E9E9' };
    },

    // -- Navigation --
    async drillInto(group) {
      const detailFilters = !this.presetStructureModified && this.currentView && this.currentView.detailFilters;
      if (detailFilters && !this._drillFilterSnapshot) {
        const snap = {};
        for (const key of Object.keys(detailFilters)) {
          snap[key] = Array.isArray(this[key]) ? [...this[key]] : this[key];
        }
        this._drillFilterSnapshot = snap;
        for (const [key, value] of Object.entries(detailFilters)) {
          this[key] = Array.isArray(value) ? [...value] : value;
        }
      }
      this.selectedGroup = group;
      this.selectedOpportunity = null;
      await this.fetchOpportunities();
    },
    navigateBack() {
      // Restore pre-drill filter state if a detail-filter overlay was applied.
      if (this._drillFilterSnapshot) {
        for (const [key, value] of Object.entries(this._drillFilterSnapshot)) {
          this[key] = Array.isArray(value) ? [...value] : value;
        }
        this._drillFilterSnapshot = null;
      }
      this.selectedGroup = null;
      this.selectedOpportunity = null;
      this.opportunityRows = [];
    },
    onRowClick(row) {
      if (this.showGroupTable) {
        if (!this.drilldownEnabled) return;
        this.drillInto(row);
      } else {
        this.selectedOpportunity = row;
      }
    },

    // -- Data fetching --
    _buildRequestPayload(extra = {}) {
      return {
        grain: this.activeGrain,
        entity_filters: this.entityFilters || undefined,
        metricFilter: this.activeMetricFilter.length > 0
          ? this.activeMetricFilter
          : VISIBLE_PROGRAMS,
        statusFilter: this.filterVal(this.activeStatusFilter),
        tierFilter: this.filterVal(this.activeTierFilter),
        coderFilter: this.filterVal(this.activeCoderFilter),
        qaResultFilter: this.activeGrain === 'code' ? this.filterVal(this.activeQaResultFilter) : undefined,
        performanceYearFilter: this.filterVal(this.activePerformanceYearFilter),
        staleOnly: this.activeStaleOnly || undefined,
        // Sent on every grain: the backend resolves both against the owning
        // opportunity, so a hidden chart takes its tasks and codes with it.
        includeExpired: this.activeIncludeExpired,
        includeAdminClosed: this.activeAdminClosed,
        dateRange: (this.dateStart || this.dateEnd) ? { start: this.dateStart, end: this.dateEnd } : undefined,
        infoTagFilters: this.infotagFilters || undefined,
        ...extra,
      };
    },
    async fetchGroups() {
      this.groupLoading = true;
      this.groupError = null;
      try {
        const response = await this.$axios.post('/api/coding/reporting/', this._buildRequestPayload({
          startRow: 0,
          endRow: 500,
          groupBy: this.groupChips,
          groupKeys: [],
        }));
        this.groupRows = response.data.rows || [];
      } catch (error) {
        console.error('Failed to fetch group data:', error);
        this.groupRows = [];
        this.groupError = error.response?.data?.error
          || 'Couldn’t load this view. The request failed — try Apply again.';
      } finally {
        this.groupLoading = false;
      }
    },

    async fetchOpportunities() {
      this.oppLoading = true;
      this.oppError = null;
      try {
        const extra = { startRow: 0, endRow: 500 };
        if (this.selectedGroup) {
          extra.groupBy = this.groupChips;
          extra.groupKeys = this.groupChips.map((_, i) => this.selectedGroup[`group_${i + 1}`]);
        }
        const response = await this.$axios.post('/api/coding/reporting/', this._buildRequestPayload(extra));
        const rows = response.data.rows || [];
        this.opportunityRows = rows.map(row => ({
          ...row,
          patient_name: [row.patient_last_name, row.patient_first_name].filter(Boolean).join(', '),
        }));
        this.oppTotalRows = response.data.totalRows || 0;
      } catch (error) {
        console.error('Failed to fetch opportunities:', error);
        this.opportunityRows = [];
        this.oppError = error.response?.data?.error
          || 'Couldn’t load this view. The request failed — try Apply again.';
      } finally {
        this.oppLoading = false;
      }
    },

    refetchCurrentLevel() {
      if (this.selectedGroup) {
        this.fetchOpportunities();
      } else {
        this.fetchGroups();
      }
    },

    // -- Filter handlers --
    _applyDatePreset(preset) {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth();
      // Local YYYY-MM-DD. Avoid toISOString(), which converts to UTC and rolls
      // the date forward in the evening for negative-UTC timezones (US).
      const toISO = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      // The backend treats the end bound as inclusive of the whole day, so each
      // preset's end is the actual last calendar day of its range.
      if (preset === 'yesterday') {
        const y = new Date(year, month, now.getDate() - 1);
        this.dateStart = toISO(y);
        this.dateEnd = toISO(y);
      } else if (preset === 'mtd') {
        this.dateStart = toISO(new Date(year, month, 1));
        this.dateEnd = toISO(now);
      } else if (preset === 'ytd') {
        this.dateStart = `${year}-01-01`;
        this.dateEnd = toISO(now);
      } else if (preset === 'last_month') {
        this.dateStart = toISO(new Date(year, month - 1, 1));
        this.dateEnd = toISO(new Date(year, month, 0));
      }
      this.activeDatePreset = preset;
    },
    setDatePreset(preset) {
      this._applyDatePreset(preset);
      this.markPending();
    },
    datePresetStyle(key) {
      const active = this.activeDatePreset === key;
      return active
        ? { backgroundColor: '#FEF3F0', color: '#F76734', border: '1px solid #F76734', fontWeight: 600 }
        : { backgroundColor: '#F9FAFB', color: '#626264', border: '1px solid #E8E9E9' };
    },

    markPending() {
      this.pendingChanges = true;
    },
    isActiveProgramTab(value) {
      if (value === null) return this.activeMetricFilter.length === 0;
      return this.activeMetricFilter.length === 1 && this.activeMetricFilter[0] === value;
    },
    selectProgramTab(value) {
      const next = value === null ? [] : [value];
      this.activeMetricFilter = next;
      this.selectedOpportunity = null;
      this.refetchCurrentLevel();
    },
    onDateInputChange() {
      // Manually entered dates clear the active preset
      this.activeDatePreset = null;
      this.markPending();
    },
    async applyPending() {
      this.selectedOpportunity = null;
      this.pendingChanges = false;
      await this.refetchCurrentLevel();
    },

    // -- Saved views (#1391) --
    closeViewMenu() {
      this.showViewMenu = false;
      this.cancelSaveAs();
    },
    selectView(key) {
      const view = this.allViews.find(v => v.key === key);
      if (!view) return;
      this._applyViewState(view.state);
      this.currentViewKey = key;
      this.pendingChanges = false;
      this.selectedGroup = null;
      this.selectedOpportunity = null;
      this.showViewMenu = false;
      saveLastViewKey(key);
      this.refetchCurrentLevel();
    },
    _applyViewState(s) {
      this.activeGrain = s.activeGrain;
      this.groupChips = (s.groupChips || [])
        .map(k => resolveGroupChip(k, this.entityTypes))
        .filter(v => v != null);
      this.activeMetricFilter = [...(s.activeMetricFilter || [])];
      this.activeStatusFilter = [...(s.activeStatusFilter || [])];
      this.activeTierFilter = [...(s.activeTierFilter || [])];
      this.activeCoderFilter = [...(s.activeCoderFilter || [])];
      this._stripCoderDimsIfOppGrain();
      this.activeQaResultFilter = [...(s.activeQaResultFilter || [])];
      this.activeStaleOnly = !!s.activeStaleOnly;
      this.activeIncludeExpired = !!s.activeIncludeExpired;
      this.activeAdminClosed = !!s.activeAdminClosed;
      if (s.activeDatePreset) {
        this._applyDatePreset(s.activeDatePreset);
      } else {
        this.activeDatePreset = null;
        this.dateStart = s.dateStart || null;
        this.dateEnd = s.dateEnd || null;
      }
    },
    _captureCurrentState() {
      // Dates are derived when a preset is active; null them out so views with
      // the same preset compare equal regardless of when they were saved.
      const presetActive = !!this.activeDatePreset;
      return {
        activeGrain: this.activeGrain,
        groupChips: this.groupChips
          .map(c => serializeGroupChip(c, this.entityTypes))
          .filter(c => c != null),
        activeMetricFilter: [...this.activeMetricFilter],
        activeStatusFilter: [...this.activeStatusFilter],
        activeTierFilter: [...this.activeTierFilter],
        activeCoderFilter: [...this.activeCoderFilter],
        activeQaResultFilter: [...this.activeQaResultFilter],
        activeStaleOnly: this.activeStaleOnly,
        activeIncludeExpired: this.activeIncludeExpired,
        activeAdminClosed: this.activeAdminClosed,
        activeDatePreset: this.activeDatePreset,
        dateStart: presetActive ? null : this.dateStart,
        dateEnd: presetActive ? null : this.dateEnd,
      };
    },
    openSaveAs() {
      this.saveAsName = '';
      this.showSaveAsInput = true;
      this.$nextTick(() => {
        if (this.$refs.saveAsInput) this.$refs.saveAsInput.focus();
      });
    },
    cancelSaveAs() {
      this.showSaveAsInput = false;
      this.saveAsName = '';
    },
    confirmSaveAs() {
      const name = this.saveAsName.trim();
      if (!name) return;
      const key = `user_${Date.now()}`;
      this.userViews.push({ key, name, state: this._captureCurrentState() });
      saveUserViews(this.userViews);
      this.currentViewKey = key;
      this.pendingChanges = false;
      saveLastViewKey(key);
      this.cancelSaveAs();
      this.showViewMenu = false;
    },
    overwriteCurrentView() {
      if (!this.canSaveOverwrite) return;
      const idx = this.userViews.findIndex(v => v.key === this.currentViewKey);
      if (idx < 0) return;
      this.userViews[idx] = {
        ...this.userViews[idx],
        state: this._captureCurrentState(),
      };
      saveUserViews(this.userViews);
      this.pendingChanges = false;
      this.showViewMenu = false;
    },
    deleteUserView(key) {
      this.userViews = this.userViews.filter(v => v.key !== key);
      saveUserViews(this.userViews);
      if (this.currentViewKey === key) {
        this.currentViewKey = null;
        saveLastViewKey(null);
      }
    },
    filterVal(arr) {
      // Return null (omit filter) when nothing selected, else return the array
      return arr && arr.length > 0 ? arr : undefined;
    },

    // -- Sorting --
    toggleSort(field) {
      if (this.showGroupTable) {
        if (this.groupSortCol === field) {
          this.groupSortDir = this.groupSortDir === 'asc' ? 'desc' : 'asc';
        } else {
          this.groupSortCol = field;
          this.groupSortDir = 'desc';
        }
      } else {
        if (this.oppSortCol === field) {
          this.oppSortDir = this.oppSortDir === 'asc' ? 'desc' : 'asc';
        } else {
          this.oppSortCol = field;
          this.oppSortDir = 'desc';
        }
      }
    },
    sortIndicator(field) {
      if (this.currentSortCol !== field) return '';
      return this.currentSortDir === 'asc' ? '▲' : '▼';
    },
    sortData(data, col, dir) {
      const d = dir === 'asc' ? 1 : -1;
      return [...data].sort((a, b) => {
        const va = a[col];
        const vb = b[col];
        if (va == null && vb == null) return 0;
        if (va == null) return 1;
        if (vb == null) return -1;
        if (typeof va === 'string') return d * va.localeCompare(vb);
        return d * (va - vb);
      });
    },

    // -- Tier progression --
    parseTierProgression(row) {
      const tiers = ['1LR', '2LR', '3LR', 'Confirm'];
      const progression = row.tier_progression || '';
      const currentTier = row.current_tier;
      const oppDone = row.opp_status === 'completed' || row.opp_status === 'expired';
      return tiers
        .filter(t => progression.includes(t))
        .map(t => ({
          name: t,
          completed: progression.includes(t + '✓'),
          isCurrent: t === currentTier,
          ended: t === currentTier && oppDone && !progression.includes(t + '✓'),
        }));
    },
    tierBadgeStyle(tier) {
      if (tier.completed) {
        return { backgroundColor: '#ECFDF5', color: '#059669', fontWeight: 600 };
      }
      if (tier.ended) {
        return { backgroundColor: '#F3F4F6', color: '#6B7280', fontWeight: 600 };
      }
      if (tier.isCurrent) {
        return { backgroundColor: '#FEF3F0', color: '#F76734', fontWeight: 600 };
      }
      return { backgroundColor: '#F3F4F6', color: '#9CA3AF' };
    },

    // -- Cell rendering --
    formatGroupCell(field, value) {
      if (value == null) return 'Unassigned';
      const idx = parseInt(field.split('_')[1], 10) - 1;
      if (this.groupChips[idx] === 'program') {
        return PROGRAM_LABELS[value] || value;
      }
      return value;
    },
    cellValue(col, row) {
      return col.compute ? col.compute(row) : row[col.field];
    },
    formatCell(col, value) {
      if (col.format === 'pct') {
        if (value == null || value === '') return '–';
        return `${Math.round(value * 100)}%`;
      }
      if (value == null || value === '') return '';
      if (typeof col.format === 'function') return col.format(value);
      if (col.format === 'boolean') return value ? 'Yes' : '';
      if (col.format === 'capitalize') {
        const s = String(value).replace(/_/g, ' ');
        return s.charAt(0).toUpperCase() + s.slice(1);
      }
      if (col.format === 'text') return String(value);
      if (typeof value === 'number') return Number(value).toLocaleString();
      return String(value);
    },
    cellStyle(col, value) {
      const style = { color: '#353537' };
      if (col.color && value != null) {
        const c = col.color(value);
        if (c) style.color = c;
      }
      if (col.bold && col.bold(value)) style.fontWeight = 600;
      if (col.small) {
        style.fontSize = '12px';
        style.letterSpacing = '0.5px';
      }
      return style;
    },

    // -- Export --
    csvEscape(val) {
      if (val == null) return '';
      const str = String(val);
      return str.includes(',') || str.includes('"') || str.includes('\n')
        ? `"${str.replace(/"/g, '""')}"` : str;
    },
    oppRow(row) {
      return this.activeColumns.map(c => this.csvEscape(this.formatCell(c, this.cellValue(c, row))));
    },
    async _fetchExportRows() {
      // Fetch all detail rows (no group filtering) for code/action exports.
      // Uses the same filters but requests detail rows with a large limit.
      const params = this._buildRequestPayload({
        startRow: 0,
        endRow: EXPORT_ROW_LIMIT + 1,
        groupBy: [],
        groupKeys: ['__all__'],
      });
      params.export = true;
      if (this.selectedGroup) {
        params.groupBy = this.groupChips;
        params.groupKeys = this.groupChips.map((_, i) => this.selectedGroup[`group_${i + 1}`]);
      }
      const response = await this.$axios.post('/api/coding/reporting/', params);
      const fetched = response.data.rows || [];
      this.exportTruncated = fetched.length > EXPORT_ROW_LIMIT;
      // Derive the display name the same way the on-screen table does
      // (fetchOpportunities). The export path bypasses that mapping, which is
      // why the Patient column came out blank in exported CSVs.
      return fetched.slice(0, EXPORT_ROW_LIMIT).map(row => ({
        ...row,
        patient_name: [row.patient_last_name, row.patient_first_name].filter(Boolean).join(', '),
      }));
    },
    async exportCurrentGrain() {
      const ticket = beginSensitiveEgress('export');
      if (!ticket) return;
      if (this.showGroupTable) {
        // Export the group summary table as-is
        const rows = this.activeRows;
        const cols = this.activeColumns;
        const header = cols.map(c => c.header).join(',');
        const body = rows.map(row =>
          cols.map(c => this.csvEscape(this.formatCell(c, this.cellValue(c, row)))).join(',')
        ).join('\n');
        this.downloadCsv(header + '\n' + body, this.activeGrain + '_groups', ticket);
        return;
      }
      this.exportLoading = true;
      this.exportError = null;
      this.exportTruncated = false;
      try {
        const needsFetch = this.oppTotalRows > this.opportunityRows.length;
        const rows = needsFetch ? await this._fetchExportRows() : this.activeRows;
        const cols = this.grainDetailColumns;
        const header = cols.map(c => c.header).join(',');
        const body = rows.map(row =>
          cols.map(c => this.csvEscape(this.formatCell(c, this.cellValue(c, row)))).join(',')
        ).join('\n');
        this.downloadCsv(header + '\n' + body, this.activeGrain + 's', ticket);
      } catch (e) {
        this.exportError = 'Export failed. Try again with narrower filters.';
      } finally {
        this.exportLoading = false;
      }
    },
    async exportDetails() {
      // Top-level export of the underlying detail rows across all groups for the
      // active grain. On the code grain this includes reject reason + explanation,
      // so it yields every coder's rejection reasons in one CSV (respecting the
      // active QA Result filter) without drilling in per coder.
      // Use the same columns the drilldown table shows (saved-view detail columns
      // if present, else the grain default) so the export mirrors the view.
      const ticket = beginSensitiveEgress('export');
      if (!ticket) return;
      this.exportLoading = true;
      this.exportError = null;
      this.exportTruncated = false;
      try {
        const rows = await this._fetchExportRows();
        const usePresetCols = this.currentView && this.currentView.columns && !this.presetStructureModified;
        const presetCols = usePresetCols ? this.currentView.columns : null;
        const visible = c => !c.showIfState || c.showIfState(this);
        const cols = ((presetCols && presetCols.detail) || this.grainDetailColumns).filter(visible);
        const header = cols.map(c => c.header).join(',');
        const body = rows.map(row =>
          cols.map(c => this.csvEscape(this.formatCell(c, this.cellValue(c, row)))).join(',')
        ).join('\n');
        this.downloadCsv(header + '\n' + body, this.activeGrain + '_details', ticket);
      } catch (e) {
        this.exportError = 'Export failed. Try again with narrower filters.';
      } finally {
        this.exportLoading = false;
      }
    },
    downloadCsv(content, suffix = 'export', ticket = null) {
      if (ticket && !ticket.confirm()) return;
      const blob = new Blob([content], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `coding_ops_${suffix}_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
  },

  async mounted() {
    const etResponse = await this.$axios.get('/api/entity-types/', { params: { include_counts: true } });
    this.entityTypes = (etResponse.data || []).filter(et => et.entity_count > 0);

    // Fetch coders in background — not required for initial render
    this.$axios.get('/api/coding/coders/').then(r => {
      this.availableCoders = r.data || [];
    }).catch(() => {});

    // Restore last-used view if still valid; otherwise default to Practice grouping
    const lastKey = loadLastViewKey();
    const lastView = lastKey ? this.allViews.find(v => v.key === lastKey) : null;
    if (lastView) {
      this._applyViewState(lastView.state);
      this.currentViewKey = lastKey;
    } else {
      const practice = this.entityTypes.find(et => et.type_name === 'Practice');
      if (practice) this.groupChips = [practice.id];
    }

    await this.fetchGroups();
  },
  beforeUnmount() {

  },
};
</script>
